import { FEET_TO_METERS, LOCATION_CONFIG } from 'apps/web_forms/utils/constants';
import { getDistance, isPointWithinRadius } from 'geolib';

const { LOCATION_LOAD_TIME_LIMIT } = LOCATION_CONFIG;
const KILOMETER = 1000;

/**
 * Return a promise to get user location thru browser API
 * @returns Promise
 */
export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    function startGettingPosition() {
      // Set time limit
      setTimeout(
        () => reject(new Error('Location load timeout')),
        LOCATION_LOAD_TIME_LIMIT
      );

      navigator.geolocation.getCurrentPosition(
        (userPosition) => {
          resolve({
            latitude: userPosition.coords.latitude,
            longitude: userPosition.coords.longitude,
            altitude: userPosition.coords.altitude,
            accuracy: userPosition.coords.accuracy,
            altitudeAccuracy: userPosition.coords.altitudeAccuracy,
          });
        },
        (err) => reject(err)
      );
    }

    if (navigator.geolocation) {
      if (navigator.permissions) {
        // Check if the user has the location disabled
        navigator.permissions
          .query({ name: 'geolocation' })
          .then(function (PermissionStatus) {
            if (!['granted', 'prompt'].includes(PermissionStatus.state)) {
              reject(new Error('Location permission denied'));
            } else {
              startGettingPosition();
            }
          });
      } else {
        startGettingPosition();
      }
    } else {
      reject(new Error(`Browser doesn't support geolocation`));
    }
  });
}

/**
 * Parse a string to get latitude and longitude values
 * @param {string} locationString
 * @returns Object
 */
export function locationParse(locationString) {
  if (locationString === undefined) return null;
  const expression = new RegExp(/(-?[0-9.]+).+?(-?[0-9.]+)/g);
  const match = Array.from(locationString.matchAll(expression));
  return {
    latitude: match[0]?.[1],
    longitude: match[0]?.[2],
  };
}

/**
 * Calculate the distance from coordinate A to B with a default accuracy of 1 and return a string with distance + unit of length
 * @param {object} start
 * @param {object} end
 * @param {number} accuracy
 * @returns String
 */
export function calculateDistance(start, end, accuracy = 1) {
  let distanceInMeters = getDistance(start, end, accuracy);

  if (distanceInMeters >= KILOMETER) {
    const distanceInKilometers = distanceInMeters / KILOMETER;
    distanceInMeters = `${distanceInKilometers} kilometers`;
  } else {
    distanceInMeters = `${distanceInMeters} meters`;
  }
  return distanceInMeters;
}

/**
 * Returns true or false if point is within a X radius based on meters from center parameter
 * @param {object} point
 * @param {object} center
 * @param {number} radius
 * @returns Bool
 */
export function isWithinRadius(point, center, radius) {
  return isPointWithinRadius(point, center, radius);
}

/**
 * Build an object with distance between two points and if the origin location is within an optial radius
 * @param {object} originLocation Ex: {latitude: -23.453, longitude: -23.454}
 * @param {object} destinationLocation Ex: {latitude: -23.453, longitude: -23.454}
 * @param {bool} failedGettingLocation
 * @param {object} radius Ex: {value: 2000, unit: "ft"}
 * @param {boolean} initiallyOutOfRange
 * @returns Object
 */
export function buildDistanceObject(
  originLocation,
  destinationLocation,
  failedGettingLocation = false,
  radius = null,
  initiallyOutOfRange = false
) {
  const distance = {
    result: null,
    error: false,
    outOfRange: initiallyOutOfRange,
  };

  if (originLocation && destinationLocation) {
    distance.result = calculateDistance(originLocation, destinationLocation);

    if (radius) {
      let length;

      switch (radius.unit) {
        case 'ft':
          length = radius.value / FEET_TO_METERS;
          break;
        default:
          length = radius.value;
          break;
      }

      const result = isWithinRadius(
        originLocation,
        destinationLocation,
        length
      );

      distance.outOfRange = !result;
    }
  }
  if (failedGettingLocation) {
    distance.error = true;
  }
  return distance;
}
